import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { REZULTATE_RMA, STARI_RMA, TIPURI_GARANTIE, eticheta } from "./enums.js";
import { data } from "./text.js";
import { tr } from "./i18n.js";

const GESTIUNE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR"];
const GESTIUNE_RMA = [...GESTIUNE, "MAGAZIONER"];
const ANUNTURI = ["ADMIN_TENANT", "MANAGER_TEHNIC"];
const eClient = (rol) => String(rol).startsWith("CLIENT_");


/** Cât a mai rămas, citit ca un om — și cât de tare trebuie să sară în ochi. */
function ramas(zile) {
  if (zile < 0) return { text: tr("expirată"), clasa: "vechime-90" };
  if (zile === 0) return { text: tr("expiră azi"), clasa: "vechime-90" };
  if (zile <= 30) return { text: `${zile} ${zile === 1 ? "zi" : "zile"}`, clasa: "vechime-60" };
  if (zile <= 60) return { text: tr("{0} de zile", zile), clasa: "vechime-30" };
  return { text: tr("{0} de zile", zile), clasa: "" };
}

export default function Garantii({ eu }) {
  const [sub, setSub] = useState("garantii");
  const poateScrie = GESTIUNE.includes(eu.rol);
  const poateRma = GESTIUNE_RMA.includes(eu.rol);
  const client = eClient(eu.rol);

  return (
    <div className="continut">
      <h2>{tr("Garanții și returi")}</h2>
      <p className="subtitlu">
        {tr("Cât mai e acoperit fiecare echipament, ce expiră curând, și ce piese sunt plecate la furnizor.")}
      </p>

      <div className="taburi-sisteme">
        <button className={`tab-sistem ${sub === "garantii" ? "activ" : ""}`}
          onClick={() => setSub("garantii")}>{tr("Garanții")}</button>
        <button className={`tab-sistem ${sub === "expira" ? "activ" : ""}`}
          onClick={() => setSub("expira")}>{tr("Ce expiră")}</button>
        {!client && (
          <button className={`tab-sistem ${sub === "rma" ? "activ" : ""}`}
            onClick={() => setSub("rma")}>{tr("Returi la furnizor")}</button>
        )}
      </div>

      {sub === "garantii" && <ListaGarantii poateScrie={poateScrie} />}
      {sub === "expira" && <CeExpira poateAnunta={ANUNTURI.includes(eu.rol)} />}
      {sub === "rma" && !client && <ListaRma poateScrie={poateRma} />}
    </div>
  );
}

function ListaGarantii({ poateScrie }) {
  const [lista, setLista] = useState([]);
  const [inactive, setInactive] = useState(false);
  const [eroare, setEroare] = useState("");
  const [incarca, setIncarca] = useState(true);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try { setLista(await api.garantii({ inactive })); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [inactive]);

  useEffect(() => { reincarca(); }, [reincarca]);

  async function stinge(g) {
    try { await api.actualizeazaGarantie(g.id, { activ: !g.activ }); await reincarca(); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  if (incarca) return <div className="gol">{tr("Se încarcă…")}</div>;

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}

      {/* Dezactivat ≠ șters: elementul rămâne găsibil, cu drum înapoi. */}
      <label className="comutator-dezactivate">
        <input type="checkbox" checked={inactive}
          onChange={(e) => setInactive(e.target.checked)} />
        {tr("arată și garanțiile stinse")}
      </label>

      <div className="panou tabel-derulabil">
        {lista.length === 0 ? (
          <div className="gol">{tr("Nicio garanție înregistrată.")}</div>
        ) : (
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Echipament")}</th><th>{tr("Client")}</th><th>{tr("Tip")}</th>
                <th>{tr("De la")}</th><th>{tr("Până la")}</th><th>{tr("Stare")}</th>
                {poateScrie && <th></th>}
              </tr>
            </thead>
            <tbody>
              {lista.map((g) => {
                const r = ramas(g.zilePanaLaExpirare);
                return (
                  <tr key={g.id} className={g.activ ? "" : "tip-dezactivat"}>
                    <td>
                      {g.echipament.eticheta ?? g.echipament.serie
                        ?? g.echipament.tipComponenta.denumire}
                      <span className="cod-mic">{tr(g.echipament.locatie.denumire)}</span>
                    </td>
                    <td>{tr(g.client.denumire)}</td>
                    <td>{eticheta(TIPURI_GARANTIE, g.tip)}</td>
                    <td>{data(g.incepeLa)}</td>
                    <td>{data(g.expiraLa)}</td>
                    <td>
                      {!g.activ ? <span className="marcaj">{tr("stinsă")}</span>
                        : <span className={r.clasa}>{r.text}</span>}
                    </td>
                    {poateScrie && (
                      <td>
                        <button className="buton buton-mic buton-sters"
                          onClick={() => stinge(g)}>
                          {g.activ ? tr("stinge") : tr("reactivează")}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function CeExpira({ poateAnunta }) {
  const [zile, setZile] = useState(60);
  const [lista, setLista] = useState([]);
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [lucreaza, setLucreaza] = useState(false);

  const reincarca = useCallback(async () => {
    try { setLista(await api.garantiiCareExpira(zile)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); }
  }, [zile]);

  useEffect(() => { reincarca(); }, [reincarca]);

  async function anunta() {
    setLucreaza(true); setMesaj(""); setEroare("");
    try {
      const r = await api.anuntaExpirari();
      setMesaj(r.trimise === 0
        ? tr("Nimic nou de anunțat — toate expirările apropiate au fost deja semnalate.")
        : `${r.trimise} ${r.trimise === 1 ? "anunț trimis" : "anunțuri trimise"}.`);
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}
      {mesaj && <div className="panou panou-atentie"><div className="panou-corp">{mesaj}</div></div>}

      <div className="linie-actiune">
        <label className="camp">
          {tr("fereastră")}
          <select value={zile} onChange={(e) => setZile(Number(e.target.value))}>
            <option value={30}>{tr("30 de zile")}</option>
            <option value={60}>{tr("60 de zile")}</option>
            <option value={90}>{tr("90 de zile")}</option>
          </select>
        </label>
        {poateAnunta && (
          <button className="buton buton-actiune" disabled={lucreaza} onClick={anunta}>
            {tr("trimite anunțurile")}
          </button>
        )}
      </div>

      <div className="panou tabel-derulabil">
        {lista.length === 0 ? (
          <div className="gol">{tr("Nicio garanție nu expiră în fereastra asta.")}</div>
        ) : (
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Echipament")}</th><th>{tr("Client")}</th><th>{tr("Locație")}</th>
                <th>{tr("Expiră")}</th><th>{tr("Rămas")}</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((g) => {
                const r = ramas(g.zile);
                return (
                  <tr key={g.id}>
                    <td>
                      {g.echipament.eticheta ?? g.echipament.serie
                        ?? g.echipament.tipComponenta.denumire}
                      <span className="cod-mic">{tr(g.echipament.sistemInstalat?.denumire)}</span>
                    </td>
                    <td>{tr(g.client.denumire)}</td>
                    <td>{tr(g.echipament.locatie.denumire)}</td>
                    <td>{data(g.expiraLa)}</td>
                    <td><span className={r.clasa}>{r.text}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="subtitlu">
        {tr("Un echipament care iese din garanție e și un moment comercial: e clientul căruia i se poate propune un contract de mentenanță înainte să rămână descoperit.")}
      </p>
    </>
  );
}

function ListaRma({ poateScrie }) {
  const [lista, setLista] = useState([]);
  const [status, setStatus] = useState("");
  const [eroare, setEroare] = useState("");
  const [deschis, setDeschis] = useState(null);

  const reincarca = useCallback(async () => {
    try { setLista(await api.rmauri(status ? { status } : {})); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); }
  }, [status]);

  useEffect(() => { reincarca(); }, [reincarca]);

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}

      <div className="linie-actiune">
        <label className="camp">
          {tr("stare")}
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{tr("toate")}</option>
            {Object.entries(STARI_RMA).map(([v, t]) => (
              <option key={v} value={v}>{tr(t)}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="panou tabel-derulabil">
        {lista.length === 0 ? (
          <div className="gol">{tr("Niciun retur.")}</div>
        ) : (
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Număr")}</th><th>{tr("Echipament")}</th><th>{tr("Client")}</th><th>{tr("Furnizor")}</th>
                <th>{tr("Stare")}</th><th>{tr("Trimis")}</th><th>{tr("Revenit")}</th><th>{tr("Rezultat")}</th>
                {poateScrie && <th></th>}
              </tr>
            </thead>
            <tbody>
              {lista.map((r) => (
                <tr key={r.id}>
                  <td>{r.numar}</td>
                  <td>
                    {r.echipament.eticheta ?? r.echipament.serie
                      ?? r.echipament.tipComponenta.denumire}
                    <span className="cod-mic">{tr(r.echipament.locatie.denumire)}</span>
                  </td>
                  <td>{tr(r.client.denumire)}</td>
                  <td>
                    {r.furnizor}
                    {r.numarRmaFurnizor && <span className="cod-mic">{r.numarRmaFurnizor}</span>}
                  </td>
                  <td>{eticheta(STARI_RMA, r.status)}</td>
                  <td>{data(r.trimisLa)}</td>
                  <td>{data(r.revenitLa)}</td>
                  <td>{r.rezultat ? eticheta(REZULTATE_RMA, r.rezultat) : "—"}</td>
                  {poateScrie && (
                    <td>
                      <button className="buton buton-mic buton-sters"
                        onClick={() => setDeschis(r)}>{tr("deschide")}</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {deschis && (
        <DetaliuRma rma={deschis} laInchidere={() => setDeschis(null)}
          laSchimbare={async () => { setDeschis(null); await reincarca(); }} />
      )}
    </>
  );
}

function DetaliuRma({ rma, laInchidere, laSchimbare }) {
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");
  const [rezultat, setRezultat] = useState("Reparat");
  const [inlocuitorId, setInlocuitorId] = useState("");
  const [numarFurnizor, setNumarFurnizor] = useState(rma.numarRmaFurnizor ?? "");

  const inchis = rma.status === "Revenit" || rma.status === "Anulat";

  async function fa(fn) {
    setLucreaza(true); setEroare("");
    try { await fn(); await laSchimbare(); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Retur")} {rma.numar}</h3>
        <p className="subtitlu">
          {rma.echipament.eticheta ?? rma.echipament.serie} · {tr(rma.client.denumire)} ·{" "}
          {rma.furnizor}
        </p>

        {/* Erorile apar ÎN fereastră: un banner din spatele ei nu se vede. */}
        {eroare && <div className="eroare">{eroare}</div>}

        <div className="panou"><div className="panou-corp">
          <p><strong>{tr("Motiv:")}</strong> {rma.motiv}</p>
          <p>
            <strong>{tr("Stare:")}</strong> {eticheta(STARI_RMA, rma.status)}
            {rma.trimisLa && ` · trimis ${data(rma.trimisLa)}`}
            {rma.revenitLa && ` · revenit ${data(rma.revenitLa)}`}
          </p>
          {rma.echipamentDeSchimb && (
            <p>
              <strong>{tr("Piesă de schimb montată:")}</strong>{" "}
              {rma.echipamentDeSchimb.eticheta ?? rma.echipamentDeSchimb.serie}
            </p>
          )}
          {rma.rezultat && (
            <p><strong>{tr("Rezultat:")}</strong> {eticheta(REZULTATE_RMA, rma.rezultat)}</p>
          )}
        </div></div>

        {!inchis && rma.status === "Deschis" && (
          <div className="panou"><div className="panou-corp">
            <label className="camp">
              {tr("numărul de retur al furnizorului (opțional)")}
              <input value={numarFurnizor}
                onChange={(e) => setNumarFurnizor(e.target.value)} />
            </label>
            <div className="linie-actiune">
              <button className="buton buton-actiune" disabled={lucreaza}
                onClick={() => fa(() => api.trimiteRma(rma.id, {
                  numarRmaFurnizor: numarFurnizor || undefined,
                }))}>
                {tr("marchează trimis")}
              </button>
            </div>
          </div></div>
        )}

        {!inchis && rma.trimisLa && (
          <div className="panou"><div className="panou-corp">
            <strong>{tr("Piesa s-a întors")}</strong>
            <p className="subtitlu">
              {tr("Ce se întâmplă cu parcul depinde de rezultat: reparată se întoarce funcțională, înlocuită lasă locul alteia, refuzată rămâne scoasă din uz.")}
            </p>
            <label className="camp">
              {tr("rezultat")}
              <select value={rezultat}
                onChange={(e) => setRezultat(e.target.value)}>
                {Object.entries(REZULTATE_RMA).map(([v, t]) => (
                  <option key={v} value={v}>{tr(t)}</option>
                ))}
              </select>
            </label>
            {rezultat === "Inlocuit" && (
              <label className="camp">
                {tr("identificatorul piesei venite în loc")}
                <input value={inlocuitorId}
                  placeholder={tr("id-ul echipamentului înlocuitor din parc")}
                  onChange={(e) => setInlocuitorId(e.target.value)} />
              </label>
            )}
            <div className="linie-actiune">
              <button className="buton buton-actiune" disabled={lucreaza}
                onClick={() => fa(() => api.inchideRma(rma.id, {
                  rezultat,
                  ...(rezultat === "Inlocuit" ? { echipamentInlocuitorId: inlocuitorId } : {}),
                }))}>
                {tr("închide returul")}
              </button>
            </div>
          </div></div>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide")}</button>
        </div>
      </div>
    </div>
  );
}
